import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils/cn';
import {
  CATEGORY_LABEL,
  PROMPT_CATEGORIES,
  type PromptCategory,
} from '../types';

interface PromptFilterBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  category: 'all' | PromptCategory;
  onCategoryChange: (c: 'all' | PromptCategory) => void;
  total: number;
  selectedCount?: number;
  onClearSelection?: () => void;
  onBulkDelete?: () => void;
  bulkBusy?: boolean;
}

export function PromptFilterBar({
  query,
  onQueryChange,
  category,
  onCategoryChange,
  total,
  selectedCount = 0,
  onClearSelection,
  onBulkDelete,
  bulkBusy,
}: PromptFilterBarProps) {
  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search by filename or remarks"
            className="h-9 pl-7 text-sm"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {total} {total === 1 ? 'prompt' : 'prompts'}
        </span>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <CategoryChip
          active={category === 'all'}
          onClick={() => onCategoryChange('all')}
        >
          All
        </CategoryChip>
        {PROMPT_CATEGORIES.map((c) => (
          <CategoryChip
            key={c}
            active={category === c}
            onClick={() => onCategoryChange(c)}
          >
            {CATEGORY_LABEL[c]}
          </CategoryChip>
        ))}
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
          <span className="text-xs font-semibold text-foreground">
            {selectedCount} selected
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="destructive"
            onClick={onBulkDelete}
            disabled={bulkBusy}
          >
            Delete selected
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClearSelection}
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
